import { NgClass } from '@angular/common';
import { Component, EventEmitter, forwardRef, inject, input, Input, Output } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormGroupDirective,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';

import { FormControlConstants } from '@shared/constants/form-control.constants';

import { InputErrorMessageComponent } from '../input-error-message/input-error-message.component';

@Component({
  imports: [ReactiveFormsModule, NgClass, InputErrorMessageComponent],
  providers: [
    {
      multi: true,
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
    },
    {
      multi: true,
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DatePickerComponent),
    },
  ],
  selector: 'aux-date-picker',
  styleUrls: ['../input.component.css'],
  template: `
    @if (
      {
        showError: control.invalid && (control.dirty || control.touched || fg?.submitted),
      };
      as obj
    ) {
      <div>
        @if (label) {
          <span class="mb-1 block text-xs">{{ label }}</span>
        }
        <input
          [type]="type"
          [min]="min"
          [max]="max"
          class="block h-10 w-full rounded-sm border-aux-gray-dark text-sm font-medium text-gray-400 placeholder-gray-400 shadow-sm focus-within:placeholder-gray-600 focus:border-aux-blue focus:ring-2 focus:ring-aux-blue-light-200"
          [ngClass]="{ 'is-invalid': obj.showError }"
          (blur)="onBlur()"
          [formControl]="control"
          [disabled]="control.disabled"
        />
        @if (obj.showError) {
          <aux-input-error-message [errors]="control.errors" />
        }
      </div>
    }
  `,
})
export class DatePickerComponent implements ControlValueAccessor, Validator {
  protected fg = inject(FormGroupDirective, { optional: true });

  customErrorMessages = input<Record<string, string>>({});

  @Output() blured = new EventEmitter<null | string>();
  @Input() control!: FormControl;
  @Input() label = '';
  protected readonly max = FormControlConstants.MAX_VALUE.DATE;
  protected readonly min = FormControlConstants.MIN_VALUE.DATE;
  @Input() type = 'date';

  onBlur() {
    this.control.updateValueAndValidity();
    this.onValidatorChange();
    this.blured.emit(this.control.value);
  }

  onChange = (_value: unknown) => {};

  onTouched = () => {};

  onValidatorChange = () => {};

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
    this.control.valueChanges.subscribe(fn);
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void) {
    this.onValidatorChange = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }
  }

  validate(control: AbstractControl): null | ValidationErrors {
    return control.errors;
  }

  writeValue(value: unknown): void {
    this.control.setValue(value);
  }
}
